import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "@reduxjs/toolkit";
import { getUserActivitylist } from "../../redux/Thunk/AdminModule/AdminThunk";
import {
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Alert,
} from "@mui/material";
import { FilterAlt as FilterAltIcon } from "@mui/icons-material";
import GenericTable from "../../Components/Ui_elements/GenericTable";
import Btn from "../../Components/Ui_elements/Btn";
import TextFieldComponent from "../../Components/TextFieldComponent";
import DropdownComponent from "../../Components/DropdownComponent";
import { showSnackbar } from "../../redux/Slice/snackbar/snackbarSlice";
import dayjs from "dayjs";
import { formatDate } from "../../Helper/generalhelper";

// Backend timestamps are stored in UTC. This normalizes strings that may be missing
// timezone information so dayjs can interpret them as UTC before converting to local.
const normalizeUtcDateString = dateStr => {
  if (!dateStr) return dateStr;

  const raw = String(dateStr).trim();
  const normalized = raw.replace(" ", "T");

  // Already has timezone offset or Z suffix.
  if (/Z$/.test(normalized) || /[+-]\d{2}:\d{2}$/.test(normalized)) return normalized;

  // If it looks like a datetime without timezone, assume it's UTC and append `Z`.
  if (normalized.includes("T")) return `${normalized}Z`;

  // Date-only strings (YYYY-MM-DD) - let dayjs parse as-is.
  return normalized;
};

const formatUtcDateTimeToLocal = dateStr => {
  const normalized = normalizeUtcDateString(dateStr);
  return formatDate(normalized, { formatId: 1, includeTime: true });
};

const formatDaysAgoLabel = dateStr => {
  if (!dateStr) return "";

  const normalized = normalizeUtcDateString(dateStr);
  const target = dayjs(normalized);
  if (!target.isValid()) return "";

  const todayStart = dayjs().startOf("day");
  const targetStart = target.startOf("day");

  const diffDays = targetStart.diff(todayStart, "day"); // <0 = past, >0 = future
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Tomorrow";

  const absDays = Math.abs(diffDays);
  const dayLabel = absDays === 1 ? "day" : "days";

  if (diffDays < 0) return `${absDays} ${dayLabel} ago`;

  // Future beyond tomorrow.
  return `In ${absDays} ${dayLabel}`;
};

// Memoized selector to prevent unnecessary re-renders
const selectUserActivity = createSelector(
  (state) => state.root.AdminModule,
  (adminModule) => ({
    useractivityList: adminModule.useractivityList || {
      data: [],
      currentPage: 1,
      limit: 100,
      totalRecords: 0,
    },
    loading: adminModule.loading,
    error: adminModule.error,
  })
);

const UserActivity = () => {
  const { useractivityList, loading, error } = useSelector(selectUserActivity);
  const dispatch = useDispatch();

  const [hasFilters, setHasFilters] = useState(false);
  const [pageSize, setPageSize] = useState(100);
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState({ column: "modified_date", dir: "desc" });

  const [search, setSearch] = useState({
    projectNumber: "",
    logStatus: "",
  });

  const [filterParams, setFilterParams] = useState({
    projectNumber: "",
    logStatus: "",
  });

  const apiParams = useMemo(
    () => ({
      page : page + 1,
      limit: pageSize,
      sortField: order.column,
      sortOrder: order.dir,
      ...filterParams,
    }),
    [page, pageSize, order, filterParams]
  );

  const fetchData = useCallback(() => {
    dispatch(getUserActivitylist(apiParams));
  }, [dispatch, apiParams]);

  useEffect(() => {
    if (hasFilters) {
      fetchData();
    }
  }, [fetchData, hasFilters]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSearch((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
if (!search.projectNumber?.trim() && !search.logStatus?.trim()) {
      dispatch(
        showSnackbar({
          message: "Please select the filter",
          type: "info",
        })
      );
      return

    }
    setPage(0);
    setFilterParams({ ...search });
    setHasFilters(true);
  };

  const handleReset = () => {
    const defaultValues = {
      projectNumber: "",
      logStatus: "",
    };
    setSearch(defaultValues);
    setFilterParams(defaultValues);
    setPage(0);
    setHasFilters(false);
  };

  const parseActivityDetails = useCallback((details) => {
    if (!details) return null;
    if (typeof details === "object") return details;
    try {
      return JSON.parse(details);
    } catch (e) {
      return null;
    }
  }, []);

  const formatActionText = useCallback(
    (activity) => {
      if (!activity) return "";

      const parsed = parseActivityDetails(activity.details);
      const comment =
        typeof parsed?.comment === "string" ? parsed.comment.trim() : "";
      if (comment) return comment;

      const logType =
        typeof activity.log_type === "string" ? activity.log_type.trim() : "";
      if (logType) return logType;

      const logStatus =
        typeof activity.log_status === "string"
          ? activity.log_status.trim()
          : "";
      return logStatus || "Activity recorded";
    },
    [parseActivityDetails]
  );

  const formatChangedFieldsSummary = useCallback((details) => {
    const parsed = parseActivityDetails(details);
    if (!parsed) return "";

    let changedFields = parsed.changedFields;
    if (typeof changedFields === "string") {
      try {
        changedFields = JSON.parse(changedFields);
      } catch (e) {
        return "";
      }
    }

    if (!changedFields || typeof changedFields !== "object") return "";

    const fieldNames = Object.keys(changedFields).filter(
      (key) => key !== "job_status_update"
    );
    if (!fieldNames.length) return "";

    const preview = fieldNames.slice(0, 4).join(", ");
    const remaining = fieldNames.length - 4;
    return remaining > 0
      ? `Changed: ${preview} (+${remaining} more)`
      : `Changed: ${preview}`;
  }, [parseActivityDetails]);

  const getActivityLink = useCallback((activity) => {
    if (!activity || activity.log_type === "delete") return null;

    switch (activity.log_status) {
      case "direct_cli":
        return `/viewdirect/${activity.log_type_id}`;
      case "indirect_cli":
        return `/viewindirect/${activity.log_type_id}`;
      case "external_res":
        return `/viewExternal/${activity.log_type_id}`;
      case "internal_res":
        return `/viewinternal/${activity.log_type_id}`;
      default:
        return null;
    }
  }, []);

  const columns = useMemo(
    () => [
      {
        name: "index",
        label: "#",
        options: {
          sort: false,
          customBodyRender: (value, tableMeta) =>
            (page) * pageSize + tableMeta.rowIndex + 1,
        },
      },
      {
        name: "log_title",
        label: "Project",
        options: {
          sort: true,
          customBodyRender: (value, tableMeta) => {
            const activity = useractivityList.data[tableMeta.rowIndex];
            const link = getActivityLink(activity);
            return link ? (
              <a href={link} style={{ color: "#1976d2", textDecoration: "none" }}>
                {value}
              </a>
            ) : (
              <span>{value}</span>
            );
          },
        },
      },
      {
        name: "action",
        label: "Action",
        options: {
          sort: false,
          customBodyRender: (value, tableMeta) => {
            const activity = useractivityList.data[tableMeta.rowIndex];
            if (!activity) return null;

            const actionText = formatActionText(activity);
            const changedSummary = formatChangedFieldsSummary(activity.details);

            return (
              <Box sx={{ py: 0.5 }}>
                <Typography
                  component="span"
                  sx={{ fontSize: "0.8125rem", display: "block", lineHeight: 1.35 }}
                >
                  {actionText}
                </Typography>
                {changedSummary ? (
                  <Typography
                    component="span"
                    sx={{
                      fontSize: "0.75rem",
                      color: "text.secondary",
                      display: "block",
                      lineHeight: 1.35,
                      mt: 0.25,
                    }}
                  >
                    {changedSummary}
                  </Typography>
                ) : null}
              </Box>
            );
          },
        },
      },
      {
        name: "creator",
        label: "Created By",
        options: {
          sort: true,
          customBodyRender: (value, tableMeta) => {
            const activity = useractivityList.data[tableMeta.rowIndex];
            return activity
              ? `${activity.vFirstName || ""} ${activity.vLastName || ""}`.trim()
              : "";
          },
        },
      },
      {
        name: "modified_date",
        label: "Date",
        options: {
          sort: true,
          customBodyRender: (value) => (value ? formatUtcDateTimeToLocal(value) : ""),
        },
      },
      {
        name: "days_ago",
        label: "Days Ago",
        options: {
          sort: true,
          customBodyRender: (value, tableMeta) => {
            const activity = useractivityList.data[tableMeta.rowIndex];
            if (!activity?.modified_date) return "";
            return formatDaysAgoLabel(activity.modified_date);
          },
        },
      },
    ],
    [
      page,
      pageSize,
      formatActionText,
      formatChangedFieldsSummary,
      getActivityLink,
      useractivityList.data,
    ]
  );

  const handleTableChange = useCallback((action, tableState) => {
    switch (action) {
      case "changePage":
        setPage(tableState.page);
        break;
      case "changeRowsPerPage":
        setPageSize(tableState.rowsPerPage);
        setPage(0);
        break;
      case "sort":
        setOrder({
          column: tableState.sortOrder.name,
          dir: tableState.sortOrder.direction,
        });
        break;
      default:
        break;
    }
  }, []);

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" sx={{ mb: 3 }}>
        Activity
      </Typography>

      <Box className="whitebox">
        <form onSubmit={handleSubmit}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextFieldComponent
                    fullWidth
                    name="projectNumber"
                    label="Project Number"
                    value={search.projectNumber}
                    onChange={handleInputChange}
                    placeholder="Project, scoop, job, or invoice number"
                    isNotMandatory={true}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <DropdownComponent
                    fullWidth
                    label="Log Type"
                    name="logStatus"
                    value={search.logStatus}
                    isNotMandatory={true}
                    onChange={handleInputChange}
                    options={[
                      { value: "project", label: "Project" },
                      { value: "scoop", label: "Scoop" },
                      { value: "job", label: "Job" },
                      { value: "invoice", label: "Invoice" },
                      { value: "internal_res", label: "Internal Resource" },
                      { value: "external_res", label: "External Resource" },
                    ]}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: "flex", gap: 2 }}>
                    <Btn
                      variant="contained"
                      startIcon={<FilterAltIcon />}
                      type="submit"
                      disabled={loading}
                    >
                      {loading ? "Filtering..." : "Filter"}
                    </Btn>
                    <Btn
                      variant="outlined"
                      onClick={handleReset}
                      type="button"
                      disabled={loading}
                    >
                      Refresh
                    </Btn>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </form>
      </Box>  

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error.message || "Failed to load activity data"}
        </Alert>
      )}

      <Card>
        <CardContent sx={{ p: "0px !important" }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <GenericTable
              columns={columns}
              data={hasFilters ? useractivityList.data : []}
              showAction= {false}
              options={{
                selectableRows: "none",
                serverSide: true,
                count: hasFilters ? useractivityList.totalRecords : 0,
                page: page,
                rowsPerPage: pageSize,
                rowsPerPageOptions: [10, 25, 50, 100],
                search: false,
                download: false,
                print: false,
                filter: false,
                viewColumns: false,
                sortOrder: {
                  name: order.column,
                  direction: order.dir,
                },
                onTableChange: handleTableChange
              }}
            />
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default UserActivity;
